import { useRef, type ReactNode, useId } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../brand";
import { useEscape, useFocusTrap, useBodyScrollLock } from "./overlay";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Which edge the sheet slides from. */
  side?: "right" | "bottom";
  width?: number;
}

/** Slide-over sheet / drawer. Right edge on desktop, bottom on mobile. */
export function Sheet({ open, onClose, title, children, side = "right", width = 480 }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEscape(open, onClose);
  useFocusTrap(ref, open);
  useBodyScrollLock(open);

  if (!open) return null;

  const panelStyle =
    side === "right"
      ? {
          height: "100%",
          width: "100%",
          maxWidth: width,
          marginLeft: "auto",
          animation: "vocSheetRight 400ms cubic-bezier(0.16,1,0.3,1)",
        }
      : {
          width: "100%",
          maxHeight: "85vh",
          marginTop: "auto",
          borderRadius: "12px 12px 0 0",
          animation: "vocSheetUp 400ms cubic-bezier(0.16,1,0.3,1)",
        };

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        background: "rgba(10,10,11,0.6)",
        backdropFilter: "blur(4px)",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        style={{
          background: "var(--bg-elev)",
          borderLeft: side === "right" ? "1px solid var(--border)" : undefined,
          borderTop: side === "bottom" ? "1px solid var(--border)" : undefined,
          boxShadow: "0 24px 64px -24px rgba(0,0,0,.6)",
          display: "flex",
          flexDirection: "column",
          outline: "none",
          ...panelStyle,
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <h2 id={titleId} style={{ fontSize: 16, fontWeight: 600 }}>
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              display: "grid",
              placeItems: "center",
              width: 28,
              height: 28,
              borderRadius: 6,
              border: "1px solid transparent",
              background: "transparent",
              color: "var(--fg-muted)",
              cursor: "pointer",
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </header>
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
